// src/components/booking/DetailsStep.jsx
import { SectionHeader, FormField, ConfirmRow, PhoneField } from "../ui";
import { formatDate } from "../../utils/dateUtils";
import { useAuth } from "../../context/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { useTenant } from "../../context/TenantContext";

export default function DetailsStep({ booking, onUpdate }) {
  const { service, barber, date, time, customer } = booking;
  const { user } = useAuth();
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const location = useLocation();

  const requiresPayment = tenant?.paymentMode === "online" && tenant?.stripeConnectStatus === "active";

  return (
    <div>
      <SectionHeader
        title="Your Details"
        subtitle="Almost done — fill in your info to complete the booking"
      />

      {/* Guest sign-in nudge */}
      {!user && (
        <div className="mb-6 flex items-center justify-between gap-4 px-4 py-3
                        bg-brand-50 border border-brand-200 rounded-xl">
          <p className="text-ink-600 text-sm font-sans">
            Have an account? Sign in to prefill your details and track bookings.
          </p>
          <button
            type="button"
            onClick={() => navigate("/login", { state: { from: location } })}
            className="shrink-0 text-sm font-semibold text-brand-600 hover:text-brand-800
                       underline underline-offset-2 transition-colors">
            Sign in
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* ── Form ── */}
        <div className="lg:col-span-3 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label="Full Name" required>
              <input type="text" placeholder="John Doe"
                value={customer.name}
                onChange={e => onUpdate("name", e.target.value)}
                className="form-input" autoComplete="name" />
            </FormField>

            <FormField label="Email Address" required>
              <input type="email" placeholder="you@email.com"
                value={customer.email}
                onChange={e => onUpdate("email", e.target.value)}
                className="form-input" autoComplete="email" />
            </FormField>

            <FormField label="Phone Number">
              <PhoneField
                value={customer.phone}
                onChange={val => onUpdate("phone", val)}
                placeholder="Phone number"
              />
            </FormField>
          </div>

          <FormField label="Notes">
            <textarea rows={4} placeholder="Any specific requests or preferences…"
              value={customer.notes}
              onChange={e => onUpdate("notes", e.target.value)}
              className="form-input resize-none" />
          </FormField>

          <p className="text-ink-400 text-xs font-sans">
            * Required fields. Your info is private and only shared with the business.
          </p>
        </div>

        {/* ── Summary ── */}
        <div className="lg:col-span-2">
          <div className="card p-5 lg:sticky lg:top-24">
            <h3 className="font-semibold text-ink-800 text-base mb-4 pb-3 border-b border-surface-100">
              Booking Summary
            </h3>
            <ConfirmRow label="Service"  value={service ? service.name : "—"} />
            <ConfirmRow label="Duration" value={service ? `${service.duration} min` : "—"} />
            <ConfirmRow label="With"     value={barber?.name || "—"} />
            <ConfirmRow label="Date"     value={date ? formatDate(date) : "—"} />
            <ConfirmRow label="Time"     value={time || "—"} />
            <ConfirmRow label="Total"    value={service ? `$${service.price}` : "—"} highlight />

            <p className="text-ink-400 text-xs font-sans mt-4 pt-4 border-t border-surface-100 leading-relaxed">
              {requiresPayment
                ? "Payment is processed securely online via Stripe. Refunds, if any, are issued by the business per their cancellation policy."
                : "Payment collected at the studio. Free cancellation up to 24 hrs before your appointment."}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
